import { ForgotPasswordState, RootState } from '@/store/types';
import { ActionTree, MutationTree, Module, GetterTree } from 'vuex';
import { clearErrors, forgotPasswordValidationSchema, validateSchema } from '@/utils/validation';
import axios from '@/utils/axios';
import * as NProgress from 'nprogress';
import { normalizeRefNo } from '@/utils/utils';

export const state: ForgotPasswordState = {
    refNo: '',
    email: '',
    recaptchaVerifyId: null,
    isSending: false,
    submitted: false,
    errors: {},
};

export const getters: GetterTree<ForgotPasswordState, RootState> = {
    getFieldErrors: state => (field: string) => state.errors[field],
};

export const mutations: MutationTree<ForgotPasswordState> = {
    setRefNo(state, payload) {
        state.refNo = payload.refNo;
        state.errors = clearErrors(state.errors, 'refNo');
    },
    setEmail(state, payload) {
        state.email = payload.email;
        state.errors = clearErrors(state.errors, 'email');
    },
    setRecaptchaVerifyId(state, payload) {
        state.recaptchaVerifyId = payload.recaptchaVerifyId;
        state.errors = clearErrors(state.errors);
    },
    setIsSending(state, payload) {
        state.isSending = payload.isSending;
        if (state.isSending) {
            NProgress.start();
        } else {
            NProgress.done();
        }
    },
    setSubmitted(state, payload) {
        state.submitted = payload.submitted;
    },
    setErrors(state, payload) {
        state.errors = payload.errors;
    },
    resetForm(state) {
        state.refNo = '';
        state.email = '';
        state.recaptchaVerifyId = null;
        state.isSending = false;
        state.submitted = false;
        state.errors = {};
    },
};

export const actions: ActionTree<ForgotPasswordState, RootState> = {
    async forgotPassword({ commit, state, rootState }) {
        const errors = await validateSchema(forgotPasswordValidationSchema, {
            refNo: state.refNo,
            email: state.email,
        });
        if (errors) {
            return commit('setErrors', { errors });
        }
        commit('setIsSending', { isSending: true });
        try {
            const response = await axios.post(`/${rootState.instanceId}/portal_forgot`, {
                refNo: normalizeRefNo(state.refNo),
                email: state.email,
                recaptchaVerifyId: state.recaptchaVerifyId,
            });
            if (response.data?.success === false) {
                const message = response.data?.message;
                commit('setErrors', { errors: { global: [message] } });
                return;
            }
            commit('setSubmitted', { submitted: true });
        } catch (err) {
            const message = err.response?.data?.message;
            commit('setErrors', { errors: { global: [message] } });
        } finally {
            commit('setIsSending', { isSending: false });
        }
    },
};

const ForgotPasswordStore: Module<ForgotPasswordState, RootState> = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};

export default ForgotPasswordStore;
